import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Upload, FileText, Trash2, Loader2, Leaf, Bot, User, ChevronDown, Paperclip, Sun, CloudRain, AlertTriangle, Camera, Image, Wrench } from 'lucide-react';
import { alex } from '../services/api';
import { toast } from 'sonner';

const AlexWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('chat'); // chat, documents
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: `Bonjour ! Je suis **BASS IA**, votre expert polyvalent IA. 🌱🔧

Je suis là pour vous aider dans tous les aspects de la gestion de votre terrain de golf :

**🌱 Agronomie**
• Analyser la météo et anticiper les risques
• Recommander des traitements adaptés
• Proposer des alternatives écologiques

**🔧 Mécanique**
• Diagnostiquer les pannes de machines
• Analyser des photos de pièces ou pannes
• Guider les réparations étape par étape

**💰 Gestion**
• Optimiser vos budgets et plannings
• Conseiller sur la gestion RH

📷 **Astuce** : Envoyez-moi une photo de votre machine pour un diagnostic rapide !

Comment puis-je vous aider aujourd'hui ?`
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [weather, setWeather] = useState(null);
  const [pendingImage, setPendingImage] = useState(null);
  const [pendingImagePreview, setPendingImagePreview] = useState(null);
  
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && activeTab === 'documents') {
      loadDocuments();
    }
    if (isOpen && !weather) {
      loadWeather();
    }
  }, [isOpen, activeTab]);

  const loadWeather = async () => {
    try {
      const response = await alex.getWeather();
      setWeather(response.data);
    } catch (error) {
      console.error('Erreur chargement météo:', error);
    }
  };

  const loadDocuments = async () => {
    try {
      const response = await alex.getDocuments();
      setDocuments(response.data || []);
    } catch (error) {
      console.error('Erreur chargement documents:', error);
    }
  };

  const handleSendMessage = async () => {
    if ((!inputMessage.trim() && !pendingImage) || isLoading) return;

    const userMessage = inputMessage.trim();
    const hasImage = !!pendingImage;
    
    setInputMessage('');
    
    // Ajouter le message utilisateur avec aperçu image si présent
    setMessages(prev => [...prev, { 
      role: 'user', 
      content: userMessage || (hasImage ? "📷 [Image envoyée]" : ""),
      hasImage: hasImage,
      imagePreview: pendingImagePreview
    }]);
    
    setIsLoading(true);

    try {
      let response;
      
      if (hasImage) {
        // Envoyer avec image
        response = await alex.sendMessageWithImage(userMessage, pendingImage, conversationId);
        // Nettoyer l'image après envoi
        setPendingImage(null);
        setPendingImagePreview(null);
      } else {
        // Envoyer texte seul
        response = await alex.sendMessage(userMessage, conversationId);
      }
      
      const data = response.data;
      
      if (data.error) {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: `Désolé, une erreur s'est produite : ${data.error}` 
        }]);
      } else {
        setConversationId(data.conversation_id);
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: data.response 
        }]);
      }
    } catch (error) {
      console.error('Erreur envoi message:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Désolé, je rencontre des difficultés techniques. Veuillez réessayer.' 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Vérifier le type
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }
    
    // Créer un aperçu
    const reader = new FileReader();
    reader.onload = (e) => {
      setPendingImagePreview(e.target.result);
    };
    reader.readAsDataURL(file);
    
    setPendingImage(file);
    toast.success('Image prête à être envoyée');
  };

  const cancelPendingImage = () => {
    setPendingImage(null);
    setPendingImagePreview(null);
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const response = await alex.uploadDocument(file);
      const data = response.data;
      
      if (data.error) {
        toast.error(`Erreur: ${data.error}`);
      } else {
        toast.success(`Document "${file.name}" analysé avec succès !`);
        loadDocuments();
        
        // Ajouter un message dans le chat
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `📄 J'ai analysé le document **"${file.name}"** et l'ai ajouté à ma base de connaissances.\n\n**Type:** ${data.analysis?.doc_type || 'Document'}\n\n**Résumé:** ${data.analysis?.summary || 'Document traité'}`
        }]);
      }
    } catch (error) {
      console.error('Erreur upload:', error);
      toast.error('Erreur lors de l\'upload du document');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteDocument = async (docId, filename) => {
    if (!window.confirm(`Supprimer "${filename}" de la base de connaissances ?`)) return;
    
    try {
      await alex.deleteDocument(docId);
      toast.success('Document supprimé');
      loadDocuments();
    } catch (error) {
      console.error('Erreur suppression:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const formatMessage = (content) => {
    // Convertir le markdown basique en HTML
    return content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/\n/g, '<br />');
  };

  const getWeatherIcon = () => {
    if (!weather?.current) return <Sun size={16} />;
    const condition = weather.current.condition?.toLowerCase() || '';
    if (condition.includes('pluie') || condition.includes('averse')) return <CloudRain size={16} className="text-blue-500" />;
    return <Sun size={16} className="text-yellow-500" />;
  };

  return (
    <>
      {/* Bouton flottant */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 ${
          isOpen ? 'bg-neutral-800 rotate-0' : 'bg-primary hover:bg-primary/90 hover:scale-110'
        }`}
        data-testid="alex-widget-toggle"
      >
        {isOpen ? (
          <X size={24} className="text-white" />
        ) : (
          <div className="relative">
            <Leaf size={24} className="text-white" />
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-pulse"></span>
          </div>
        )}
      </button>

      {/* Widget panel */}
      {isOpen && (
        <div 
          className="fixed bottom-24 left-3 right-3 md:left-auto md:right-6 md:w-[400px] z-50 h-[70vh] md:h-[600px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-neutral-200"
          data-testid="alex-widget-panel"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-primary to-primary/80 px-4 py-3 flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <Leaf size={20} className="text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-white font-semibold">BASS IA</h3>
              <p className="text-white/80 text-xs">Expert Agronome & Mécanicien IA</p>
            </div>
            {weather?.current && (
              <div className="flex items-center gap-1 bg-white/20 px-2 py-1 rounded-full text-white text-xs">
                {getWeatherIcon()}
                <span>{Math.round(weather.current.temperature)}°C</span>
              </div>
            )}
          </div>

          {/* Alerts météo */}
          {weather?.risks?.length > 0 && (
            <div className="px-3 py-2 bg-amber-50 border-b border-amber-100">
              <div className="flex items-center gap-2 text-amber-700 text-xs">
                <AlertTriangle size={14} />
                <span className="font-medium">{weather.risks[0].message}</span>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="flex border-b border-neutral-200">
            <button
              onClick={() => setActiveTab('chat')}
              className={`flex-1 py-2 text-sm font-medium transition-colors ${
                activeTab === 'chat' 
                  ? 'text-primary border-b-2 border-primary' 
                  : 'text-neutral-500 hover:text-neutral-700'
              }`}
            >
              <MessageCircle size={14} className="inline mr-1" />
              Chat
            </button>
            <button
              onClick={() => setActiveTab('documents')}
              className={`flex-1 py-2 text-sm font-medium transition-colors ${
                activeTab === 'documents' 
                  ? 'text-primary border-b-2 border-primary' 
                  : 'text-neutral-500 hover:text-neutral-700'
              }`}
            >
              <FileText size={14} className="inline mr-1" />
              Documents ({documents.length})
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden">
            {activeTab === 'chat' ? (
              <>
                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-3 md:p-4 space-y-3 md:space-y-4 h-[calc(70vh-220px)] md:h-[380px]">
                  {messages.map((msg, index) => (
                    <div
                      key={index}
                      className={`flex gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                    >
                      <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center ${
                        msg.role === 'user' 
                          ? 'bg-primary/10 text-primary' 
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {msg.role === 'user' ? <User size={16} /> : <Leaf size={16} />}
                      </div>
                      <div className={`max-w-[80%] ${msg.role === 'user' ? 'text-right' : ''}`}>
                        {msg.imagePreview && (
                          <div className="mb-2">
                            <img 
                              src={msg.imagePreview} 
                              alt="Image envoyée" 
                              className="max-w-full max-h-32 rounded-lg border border-neutral-200"
                            />
                          </div>
                        )}
                        {msg.content && (
                          <div 
                            className={`px-3 py-2 rounded-2xl text-sm inline-block ${
                              msg.role === 'user' 
                                ? 'bg-primary text-white rounded-tr-none' 
                                : 'bg-neutral-100 text-neutral-800 rounded-tl-none'
                            }`}
                            dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }}
                          />
                        )}
                      </div>
                    </div>
                  ))}
                  {isLoading && (
                    <div className="flex gap-2">
                      <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                        <Leaf size={16} className="text-green-700" />
                      </div>
                      <div className="bg-neutral-100 px-4 py-2 rounded-2xl rounded-tl-none">
                        <div className="flex items-center gap-2 text-neutral-500">
                          <Loader2 size={14} className="animate-spin" />
                          <span className="text-sm">BASS analyse...</span>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Pending Image Preview */}
                {pendingImagePreview && (
                  <div className="px-3 py-2 bg-blue-50 border-t border-blue-100 flex items-center gap-2">
                    <img src={pendingImagePreview} alt="Aperçu" className="w-12 h-12 object-cover rounded" />
                    <div className="flex-1">
                      <p className="text-xs text-blue-700 font-medium">Image prête à envoyer</p>
                      <p className="text-xs text-blue-600">Ajoutez un message ou envoyez directement</p>
                    </div>
                    <button
                      onClick={cancelPendingImage}
                      className="p-1 hover:bg-blue-100 rounded text-blue-600"
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}

                {/* Input */}
                <div className="p-3 border-t border-neutral-200 bg-neutral-50">
                  <div className="flex items-center gap-2">
                    {/* Document upload (hidden) */}
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      className="hidden"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                    />
                    {/* Image upload (hidden) */}
                    <input
                      type="file"
                      ref={imageInputRef}
                      onChange={handleImageSelect}
                      className="hidden"
                      accept="image/*"
                    />
                    {/* Document button */}
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                      className="p-2 hover:bg-neutral-200 rounded-lg transition-colors text-neutral-500 hover:text-neutral-700"
                      title="Ajouter un document à la base de connaissances"
                    >
                      {isUploading ? <Loader2 size={18} className="animate-spin" /> : <Paperclip size={18} />}
                    </button>
                    {/* Camera/Image button */}
                    <button
                      onClick={() => imageInputRef.current?.click()}
                      disabled={isLoading}
                      className={`p-2 rounded-lg transition-colors ${
                        pendingImage 
                          ? 'bg-blue-100 text-blue-600' 
                          : 'hover:bg-neutral-200 text-neutral-500 hover:text-neutral-700'
                      }`}
                      title="Envoyer une photo (diagnostic machine)"
                    >
                      <Camera size={18} />
                    </button>
                    <input
                      type="text"
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Posez votre question à BASS..."
                      className="flex-1 px-3 py-2 bg-white border border-neutral-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                      disabled={isLoading}
                    />
                    <button
                      onClick={handleSendMessage}
                      disabled={isLoading || !inputMessage.trim()}
                      className="p-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Send size={18} />
                    </button>
                  </div>
                </div>
              </>
            ) : (
              /* Documents tab */
              <div className="p-4 overflow-y-auto h-[440px]">
                <div className="mb-4">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    className="w-full py-3 border-2 border-dashed border-neutral-300 rounded-xl text-neutral-600 hover:border-primary hover:text-primary transition-colors flex items-center justify-center gap-2"
                  >
                    {isUploading ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        Analyse en cours...
                      </>
                    ) : (
                      <>
                        <Upload size={18} />
                        Ajouter un document
                      </>
                    )}
                  </button>
                  <p className="text-xs text-neutral-500 mt-2 text-center">
                    PDF, Word, Excel, Images acceptés
                  </p>
                </div>

                <div className="space-y-2">
                  {documents.length === 0 ? (
                    <div className="text-center py-8 text-neutral-500">
                      <FileText size={32} className="mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Aucun document</p>
                      <p className="text-xs">Uploadez des cahiers des charges, rapports, etc.</p>
                    </div>
                  ) : (
                    documents.map((doc) => (
                      <div 
                        key={doc.id} 
                        className="p-3 bg-neutral-50 rounded-lg border border-neutral-200 hover:border-neutral-300 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-start gap-2 flex-1 min-w-0">
                            <FileText size={16} className="text-primary mt-0.5 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm text-neutral-900 truncate">{doc.filename}</p>
                              <p className="text-xs text-neutral-500">{doc.doc_type}</p>
                              {doc.summary && (
                                <p className="text-xs text-neutral-600 mt-1 line-clamp-2">{doc.summary}</p>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => handleDeleteDocument(doc.id, doc.filename)}
                            className="p-1 hover:bg-red-50 rounded text-neutral-400 hover:text-red-500 transition-colors"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                        {doc.keywords?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {doc.keywords.slice(0, 5).map((kw, i) => (
                              <span key={i} className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full">
                                {kw}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default AlexWidget;
